import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Play, Instagram, Film, Code2, BarChart3,
  Palette, Crown, Check, Star, TrendingUp, Sparkles, Target
} from "lucide-react";
import Marquee from "@/components/Marquee";
import LogoMarquee from "@/components/LogoMarquee";

const MARQUEE_ITEMS = ["Content", "Creativity", "Campaigns", "Websites", "Reels", "Performance", "Branding"];

const OFFERINGS = [
  {
    icon: Instagram,
    title: "Content & Social Media Management",
    desc: "Consistent content planning, creative development, reels production, publishing and performance reporting — designed to strengthen brand visibility and engagement."
  },
  {
    icon: Film,
    title: "Reels & Creative Production",
    desc: "High-impact visual content crafted to improve engagement, brand recall and social media performance."
  },
  {
    icon: BarChart3,
    title: "Performance Marketing",
    desc: "Data-driven campaigns across Meta and Google — built to improve reach, lead generation and return on ad spend."
  },
  {
    icon: Code2,
    title: "Website Design & Development",
    desc: "Custom websites and landing pages focused on user experience, brand presentation and conversion optimisation."
  },
  {
    icon: Palette,
    title: "Branding & Content",
    desc: "Strategic brand identity, messaging, copywriting and content development that creates clarity and differentiation."
  },
  {
    icon: Crown,
    title: "Marketing Consulting",
    desc: "Senior-level marketing guidance to help businesses strengthen positioning, sharpen execution and accelerate growth."
  },
];

const REEL_WORK = [
  { tag: "Instagram Reel", title: "Lifestyle Series", client: "Atelier North", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsJTIwZGF0YSUyMGRhc2hib2FyZCUyMGFuYWx5dGljcyUyMGRhcmt8ZW58MHx8fHwxNzgwODU2MTg2fDA&ixlib=rb-4.1.0&q=85" },
  { tag: "Client Campaign Video", title: "Skincare Hero Film", client: "Vanta Co.", img: "https://images.unsplash.com/photo-1642945538257-d1e69abfb085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGx1eHVyeSUyMGRhcmslMjBnb2xkJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MDg1NjE4Nnww&ixlib=rb-4.1.0&q=85" },
  { tag: "Instagram Reel", title: "Boutique Hotel Reels", client: "Northwood", img: "https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODA4NTYxODZ8MA&ixlib=rb-4.1.0&q=85" },
  { tag: "Website Project", title: "Performance Site Build", client: "Marq Studio", img: "https://images.unsplash.com/photo-1698500278205-bf650c97a1f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODA4NTYxODZ8MA&ixlib=rb-4.1.0&q=85" },
  { tag: "Creative Work", title: "Editorial Carousel", client: "Maison 27", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsJTIwZGF0YSUyMGRhc2hib2FyZCUyMGFuYWx5dGljcyUyMGRhcmt8ZW58MHx8fHwxNzgwODU2MTg2fDA&ixlib=rb-4.1.0&q=85" },
  { tag: "Client Campaign Video", title: "Designer Brand Film", client: "Kairos", img: "https://images.unsplash.com/photo-1642945538257-d1e69abfb085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGx1eHVyeSUyMGRhcmslMjBnb2xkJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MDg1NjE4Nnww&ixlib=rb-4.1.0&q=85" },
];

const ENGAGEMENT_PILLARS = [
  { num: "01", title: "Project Scope", desc: "Sized to your ambition — from a single sprint to a long-term partnership." },
  { num: "02", title: "Content & Platforms", desc: "Built around the platforms, formats and frequency your brand actually needs." },
  { num: "03", title: "Timelines", desc: "Cadence and milestones tailored to your launch windows and growth goals." },
  { num: "04", title: "Business Outcomes", desc: "Measured against the outcomes that matter — visibility, leads, conversion, recall." },
];

const RETAINERS = [
  {
    name: "Starter", featured: false,
    features: ["8 static posts", "4 reels", "2 platforms", "Monthly social calendar", "Basic community engagement", "Monthly performance report"],
  },
  {
    name: "Growth", featured: true,
    features: ["12 static posts", "6 reels", "Strategy + content hooks", "2–3 platforms", "Competitor benchmarking", "Weekly insights dashboard", "2 ad-ready creatives"],
  },
  {
    name: "Scale", featured: false,
    features: ["16 static posts", "8 premium reels", "Multi-platform management", "4 ad creatives / month", "Monthly content strategy", "Influencer shortlist", "Basic ORM", "Performance calendar"],
  },
  {
    name: "Enterprise", featured: false,
    features: ["20–24 posts", "10 reels", "Omni-channel strategy", "Advanced analytics", "Dedicated account manager", "Quarterly brand refresh", "Full funnel management"],
  },
];

const PERFORMANCE_CORE = [
  { title: "Meta Ads Management", desc: "Build, optimise and scale campaigns designed to improve reach, engagement and lead generation." },
  { title: "Google Ads Management", desc: "Target high-intent audiences through search, display and performance campaigns." },
  { title: "High Budget Model", desc: "Advanced campaign management and optimisation for brands investing in aggressive growth initiatives." },
];

const PERFORMANCE_ADDONS = [
  { title: "Landing Page Setup", desc: "Conversion-focused landing pages designed to support campaign objectives." },
  { title: "Conversion Tracking & GTM", desc: "Reliable tracking and analytics setup for accurate performance measurement." },
  { title: "Retargeting Creative Pack", desc: "Creative assets designed to re-engage prospects and improve conversion rates." },
  { title: "Ad Copywriting", desc: "Strategic messaging crafted to improve campaign effectiveness." },
];

const WEB_BRAND = [
  { title: "Logo Design", desc: "Distinctive visual identities designed to create memorable first impressions." },
  { title: "Brand Kit", desc: "Comprehensive branding systems that ensure consistency across every touchpoint." },
  { title: "Website Content", desc: "Strategic website copy designed to communicate value clearly." },
  { title: "SEO Blog", desc: "Search-optimised content created to improve visibility and authority." },
  { title: "Social Media Captions", desc: "Platform-specific messaging crafted to improve engagement." },
  { title: "Chatbot Setup", desc: "Automated customer engagement solutions for improved responsiveness." },
  { title: "Competitor Audit", desc: "Detailed competitive analysis to identify opportunities and gaps." },
];

const CONSULTING = [
  { title: "CXO-Level Marketing Strategy", desc: "Senior-level guidance to improve marketing effectiveness and business growth." },
  { title: "GTM Sprint", desc: "Focused strategic planning designed to accelerate execution and market readiness." },
  { title: "Advisory Retainer", desc: "Ongoing marketing leadership and strategic support." },
];

const CONSULTING_BULLETS = [
  "GTM Strategy", "Marketing Funnels", "Content Roadmaps",
  "Performance Review", "Brand Positioning", "Sales Enablement Inputs"
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

export default function Home() {
  useEffect(() => { document.title = "The Indian Marketing — A Studio for Brand Craft"; }, []);

  return (
    <div data-testid="home-page" className="bg-[#2E2E31] text-white">
      {/* ====== HERO ====== */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-12">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1642945538257-d1e69abfb085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGx1eHVyeSUyMGRhcmslMjBnb2xkJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MDg1NjE4Nnww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/70 via-[#2E2E31]/80 to-[#2E2E31]" />
          <div className="absolute inset-0 grid-pattern opacity-40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="flex items-center gap-3 mb-8">
            <span className="h-px w-12 bg-[#D4AF37]" />
            <span className="font-outfit text-xs uppercase tracking-[0.32em] text-[#D4AF37]">The Indian Marketing</span>
          </motion.div>

          <motion.h1
            initial="hidden" animate="show" variants={fadeUp}
            transition={{ delay: 0.1 }}
            className="font-playfair text-[3.2rem] md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tight text-white"
          >
            A Studio for <br />
            <em className="text-[#D4AF37] font-normal">Brand</em> Craft<span className="text-[#D4AF37]">.</span>
          </motion.h1>

          <motion.p
            initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.3 }}
            className="font-outfit text-lg md:text-2xl text-[#D9D9D9] mt-10 max-w-3xl leading-relaxed"
          >
            Strategy, creativity, content and digital excellence — for brands that want to lead.
          </motion.p>

          <motion.div
            initial="hidden" animate="show" variants={fadeUp} transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-4 mt-12"
          >
            <Link
              to="/services"
              data-testid="hero-cta-explore-services"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-outfit font-medium px-8 py-4 uppercase tracking-[0.2em] text-sm hover:bg-[#F0C84B] transition-colors"
            >
              Explore Our Services <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-3 bg-transparent text-white border border-[#D4AF37] font-outfit font-medium px-8 py-4 uppercase tracking-[0.2em] text-sm hover:bg-[#D4AF37]/10 transition-colors"
            >
              Contact Us <ArrowRight size={16} />
            </Link>
          </motion.div>

          <div className="absolute -bottom-8 right-6 md:right-12 hidden md:block pointer-events-none">
            <div className="font-playfair text-[12rem] text-[#D4AF37]/5 leading-none select-none">TIM</div>
          </div>
        </div>
      </section>

      {/* ====== MARQUEE STRIP ====== */}
      <section className="border-y border-white/10 bg-[#111111] py-8" data-testid="hero-marquee">
        <Marquee items={MARQUEE_ITEMS} />
      </section>

      {/* ====== ABOUT PREVIEW ====== */}
      <section className="py-24 md:py-28 relative overflow-hidden" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="aspect-[4/5] overflow-hidden relative zoom-on-hover">
              <img
                src="https://images.unsplash.com/photo-1698500278205-bf650c97a1f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODA4NTYxODZ8MA&ixlib=rb-4.1.0&q=85"
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/60 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] text-black p-6 max-w-xs hidden md:block">
              <div className="font-playfair text-2xl leading-tight">Boutique by Choice</div>
              <div className="font-outfit text-[11px] uppercase tracking-[0.25em] mt-2">Creative by Craft</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 lg:pl-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">About the Studio</span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl text-white tracking-tight leading-[1.1]">
              Boutique by Choice. <br /> <em className="text-[#D4AF37] font-normal">Creative by Craft.</em>
            </h2>
            <p className="font-outfit text-lg text-[#D9D9D9] leading-relaxed mt-8 max-w-xl">
              We work with a select number of brands so every engagement gets senior craft, sharp creative and consistent digital growth. Aesthetics with analytics. Storytelling with performance.
            </p>
            <Link
              to="/about"
              data-testid="about-preview-cta"
              className="inline-flex items-center gap-3 mt-10 text-[#D4AF37] font-outfit text-xs uppercase tracking-[0.25em] hover:gap-5 transition-all gold-link"
            >
              Know More About Us <ArrowUpRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ====== SERVICES (solution-oriented, no pricing) ====== */}
      <section className="py-24 md:py-28 bg-[#111111] relative" data-testid="offerings-section">
        <div className="absolute inset-0 stripe-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-[#D4AF37]" />
                <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">What We Do</span>
              </div>
              <h2 className="font-playfair text-4xl md:text-6xl text-white tracking-tight leading-[1.05]">
                Six crafted services. <em className="text-[#D4AF37] font-normal">One studio.</em>
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="inline-flex items-center gap-3 text-[#D4AF37] font-outfit text-xs uppercase tracking-[0.25em] hover:gap-5 transition-all gold-link self-start md:self-end"
            >
              View All Capabilities <ArrowUpRight size={14} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {OFFERINGS.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.div
                  key={i}
                  data-testid={`offering-card-${i}`}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="group bg-[#111111] hover:bg-[#1c1c1f] p-10 card-hover relative min-h-[260px]"
                >
                  <div className="absolute top-6 right-6 font-playfair text-5xl text-[#D4AF37]/10 leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <Icon size={32} strokeWidth={1.25} className="text-[#D4AF37] mb-8 group-hover:scale-110 transition-transform duration-500" />
                  <h3 className="font-playfair text-2xl text-white mb-4 tracking-tight">{o.title}</h3>
                  <p className="font-outfit text-sm text-[#D9D9D9] leading-relaxed">{o.desc}</p>
                  <div className="mt-8 h-px w-0 group-hover:w-16 bg-[#D4AF37] transition-all duration-500" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== CLIENT LOGO STRIP ====== */}
      <section className="py-20 bg-[#111111] border-y border-white/10" data-testid="logo-strip-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 mb-10 text-center">
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Trusted Studio</span>
            <span className="h-px w-10 bg-[#D4AF37]" />
          </div>
          <h3 className="font-playfair text-3xl md:text-4xl text-white tracking-tight">
            Brands &amp; Projects We&apos;ve Supported
          </h3>
        </div>
        <LogoMarquee />
      </section>

      {/* ====== SECTION 6 — RETAINERS (no pricing) ====== */}
      <section className="py-24 md:py-28 relative" data-testid="retainers-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Monthly Retainers</span>
              <span className="h-px w-10 bg-[#D4AF37]" />
            </div>
            <h2 className="font-playfair text-4xl md:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl mx-auto">
              Retainers for Brands That Need <em className="text-[#D4AF37] font-normal">Consistency.</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {RETAINERS.map((r, i) => (
              <motion.div
                key={i}
                data-testid={`retainer-card-${r.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`relative p-8 card-hover border ${
                  r.featured
                    ? "bg-[#D4AF37]/5 border-[#D4AF37]"
                    : "bg-[#1c1c1f] border-white/10 hover:border-[#D4AF37]/60"
                }`}
              >
                {r.featured && (
                  <div className="absolute -top-3 left-6 bg-[#D4AF37] text-black font-outfit text-[10px] uppercase tracking-[0.25em] px-3 py-1 flex items-center gap-1">
                    <Star size={10} fill="currentColor" /> Most Loved
                  </div>
                )}
                <div className="font-playfair text-3xl md:text-4xl text-white tracking-tight">{r.name}</div>
                <div className="hairline mt-6 mb-6" />
                <ul className="space-y-3 mb-8">
                  {r.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 font-outfit text-sm text-[#D9D9D9]">
                      <Check size={14} className="text-[#D4AF37] mt-1 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`retainer-cta-${r.name.toLowerCase()}`}
                  className={`block text-center font-outfit font-medium px-5 py-3 uppercase tracking-[0.2em] text-xs transition-colors ${
                    r.featured
                      ? "bg-[#D4AF37] text-black hover:bg-[#F0C84B]"
                      : "border border-[#D4AF37] text-white hover:bg-[#D4AF37]/10"
                  }`}
                >
                  Inquire Now
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== SECTION 7 — PERFORMANCE MARKETING ====== */}
      <section className="py-24 md:py-28 bg-[#111111] relative" data-testid="performance-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14"
          >
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp size={20} className="text-[#D4AF37]" />
                <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Performance Marketing</span>
              </div>
              <h2 className="font-playfair text-4xl md:text-6xl text-white tracking-tight leading-[1.05]">
                Campaigns Built to <em className="text-[#D4AF37] font-normal">Drive ROI.</em>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-12">
              <p className="font-outfit text-lg text-[#D9D9D9] leading-relaxed">
                Our performance marketing services are focused on measurable results through targeted campaigns, smart optimisation, audience insights and continuous improvement.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {PERFORMANCE_CORE.map((p, i) => (
              <motion.div
                key={i}
                data-testid={`perf-core-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="p-8 bg-[#1c1c1f] border border-white/10 hover:border-[#D4AF37]/60 card-hover"
              >
                <Target size={26} className="text-[#D4AF37] mb-6" strokeWidth={1.25} />
                <h3 className="font-playfair text-2xl text-white tracking-tight mb-4">{p.title}</h3>
                <p className="font-outfit text-sm text-[#D9D9D9] leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Add-ons</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {PERFORMANCE_ADDONS.map((a, i) => (
              <div key={i} data-testid={`perf-addon-${i}`} className="bg-[#111111] p-6 hover:bg-[#1c1c1f] transition-colors">
                <div className="font-playfair text-lg text-white tracking-tight mb-2">{a.title}</div>
                <p className="font-outfit text-sm text-[#D9D9D9]/85 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== SECTION 8 — WEBSITES & BRAND ASSETS ====== */}
      <section className="py-24 md:py-28 relative" data-testid="webbrand-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="mb-14"
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={18} className="text-[#D4AF37]" />
              <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Website &amp; Branding</span>
            </div>
            <h2 className="font-playfair text-4xl md:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl">
              Websites and Brand Assets That <em className="text-[#D4AF37] font-normal">Feel Premium.</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {WEB_BRAND.map((w, i) => (
              <motion.div
                key={i}
                data-testid={`webbrand-card-${i}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-[#2E2E31] p-8 hover:bg-[#1c1c1f] transition-colors group"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <h3 className="font-playfair text-xl text-white tracking-tight group-hover:text-[#D4AF37] transition-colors">{w.title}</h3>
                  <ArrowUpRight size={18} className="text-[#D9D9D9]/40 group-hover:text-[#D4AF37] group-hover:rotate-45 transition-all duration-500 shrink-0 mt-1" />
                </div>
                <p className="font-outfit text-sm text-[#D9D9D9]/85 leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== SECTION 9 — CONSULTING ====== */}
      <section className="py-24 md:py-28 bg-[#111111] relative" data-testid="consulting-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="mb-14"
          >
            <div className="flex items-center gap-3 mb-6">
              <Crown size={18} className="text-[#D4AF37]" />
              <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Consulting</span>
            </div>
            <h2 className="font-playfair text-4xl md:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl">
              Senior-Level Marketing Direction <br /> When You Need <em className="text-[#D4AF37] font-normal">Clarity.</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {CONSULTING.map((c, i) => (
              <motion.div
                key={i}
                data-testid={`consulting-card-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="p-8 bg-[#1c1c1f] border border-white/10 hover:border-[#D4AF37]/60 card-hover"
              >
                <Crown size={26} className="text-[#D4AF37] mb-6" strokeWidth={1.25} />
                <h3 className="font-playfair text-2xl text-white tracking-tight mb-4">{c.title}</h3>
                <p className="font-outfit text-sm text-[#D9D9D9] leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="border border-[#D4AF37]/30 bg-[#2E2E31]/40 p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {CONSULTING_BULLETS.map((b, i) => (
                <div key={i} className="flex items-center gap-3 font-outfit text-base text-[#D9D9D9]">
                  <span className="text-[#D4AF37]">◆</span> {b}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====== TAILORED ENGAGEMENT MODELS ====== */}
      <section className="py-28 md:py-36 relative overflow-hidden" data-testid="engagement-models">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODA4NTYxODZ8MA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2E2E31] via-[#2E2E31]/95 to-[#2E2E31]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#D4AF37]" />
              <span className="font-outfit text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Engagement</span>
              <span className="h-px w-12 bg-[#D4AF37]" />
            </div>
            <h2 className="font-playfair text-4xl md:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl mx-auto">
              Tailored <em className="text-[#D4AF37] font-normal">Engagement Models.</em>
            </h2>
            <p className="font-outfit text-lg md:text-xl text-[#D9D9D9] mt-8 max-w-3xl mx-auto leading-relaxed">
              Every brand has unique goals, audiences and growth objectives. Our engagements are crafted around project scope, content requirements, platforms, timelines and business goals — never templated.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mb-16">
            {ENGAGEMENT_PILLARS.map((p, i) => (
              <motion.div
                key={i}
                data-testid={`engagement-pillar-${i}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-[#1c1c1f] hover:bg-[#222226] p-10 transition-colors card-hover relative"
              >
                <div className="font-playfair text-[#D4AF37] text-3xl mb-6">{p.num}</div>
                <h3 className="font-playfair text-xl md:text-2xl text-white tracking-tight mb-3">{p.title}</h3>
                <p className="font-outfit text-sm text-[#D9D9D9] leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to="/contact"
              data-testid="engagement-cta-discovery"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-outfit font-medium px-10 py-5 uppercase tracking-[0.22em] text-sm hover:bg-[#F0C84B] transition-colors"
            >
              Schedule a Discovery Call <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="engagement-cta-discuss"
              className="inline-flex items-center gap-3 bg-transparent text-white border border-[#D4AF37] font-outfit font-medium px-10 py-5 uppercase tracking-[0.22em] text-sm hover:bg-[#D4AF37]/10 transition-colors"
            >
              Let&apos;s Discuss Your Requirements <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
